'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { AuthUser, RoleName, DEMO_USERS, hasRole as checkRole, hasPermission as checkPermission } from '@/lib/auth';

type SimulatedRole = RoleName | 'MULTI_ROLE' | 'FINANCE_CASHIER';

interface AuthContextType {
  user: AuthUser;
  activeRole: SimulatedRole;
  switchRole: (role: SimulatedRole) => void;
  hasRole: (roles: RoleName[]) => boolean;
  hasPermission: (permission: string) => boolean;
  canAccess: (allowedRoles?: RoleName[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [activeRole, setActiveRole] = useState<SimulatedRole>('ADMIN');
  const user = DEMO_USERS[activeRole] || DEMO_USERS.ADMIN;

  const switchRole = (role: SimulatedRole) => {
    setActiveRole(role);
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.cookie = `x_user_id=${user.id}; path=/`;
      document.cookie = `x_user_role=${activeRole}; path=/`;

      // Intercept window.fetch to automatically include auth headers for every API request
      const originalFetch = window.fetch;
      window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
        const headers = new Headers(init?.headers || {});
        if (!headers.has('x-user-id')) {
          headers.set('x-user-id', user.id);
        }
        if (!headers.has('x-user-role')) {
          headers.set('x-user-role', activeRole);
        }
        return originalFetch(input, { ...init, headers });
      };
    }
  }, [user, activeRole]);

  const hasRole = (roles: RoleName[]) => {
    return checkRole(user.roles, roles);
  };

  const hasPermission = (permission: string) => {
    return checkPermission(user, permission);
  };

  const canAccess = (allowedRoles?: RoleName[]) => {
    if (!allowedRoles || allowedRoles.length === 0) return true;
    return checkRole(user.roles, allowedRoles);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        activeRole,
        switchRole,
        hasRole,
        hasPermission,
        canAccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
